import { CheckIcon, ListFilterIcon, LucideIcon } from "lucide-react";
import { Dispatch, SetStateAction } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  notificationCategoryToMeta,
  notificationTypeFilterToMeta,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_NOTIFICATION_SURFACES, MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  NotificationCategoryId,
  NotificationFilterState,
  NotificationType,
} from "@/types";

const dropdownSectionLabelClassName =
  "px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500";

const NOTIFICATION_CATEGORY_IDS = Object.values(NotificationCategoryId);
const FILTERABLE_NOTIFICATION_TYPES = Object.keys(
  notificationTypeFilterToMeta,
) as NotificationType[];

export const NotificationFilterButton = ({
  filterState,
  setFilterState,
  trackingSurface,
}: {
  filterState: NotificationFilterState;
  setFilterState: Dispatch<SetStateAction<NotificationFilterState>>;
  trackingSurface: MIXPANEL_NOTIFICATION_SURFACES;
}) => {
  const selectedCategoryIds = new Set(filterState.categoryIds);
  const selectedTypes = new Set(filterState.types);

  const toggleCategory = (categoryId: NotificationCategoryId) => {
    const next = new Set(selectedCategoryIds);
    if (next.has(categoryId)) {
      next.delete(categoryId);
    } else {
      next.add(categoryId);
    }
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.NOTIFICATIONS,
      "Notification category",
      categoryId,
      { is_selecting: next.has(categoryId), surface: trackingSurface },
    );
    setFilterState({ ...filterState, categoryIds: Array.from(next) });
  };

  const toggleType = (type: NotificationType) => {
    const next = new Set(selectedTypes);
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    mixpanelUtils.trackFilterAndSortOptionSelected(
      MIXPANEL_SCREENS.NOTIFICATIONS,
      "Notification type",
      type,
      { is_selecting: next.has(type), surface: trackingSurface },
    );
    setFilterState({ ...filterState, types: Array.from(next) });
  };

  const activeCount = selectedCategoryIds.size + selectedTypes.size;
  const hasActiveFilter = activeCount > 0;

  return (
    <DropdownMenu
      onOpenChange={(isOpen) => {
        if (!isOpen) return;
        mixpanelUtils.trackFilterAndSortClick(
          MIXPANEL_SCREENS.NOTIFICATIONS,
          "Notification filters",
          { surface: trackingSurface },
        );
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            "relative h-9 w-9 shrink-0 rounded-full border-slate-200 bg-white p-0 shadow-none",
            hasActiveFilter
              ? "border-primary-400 bg-primary-50 text-primary-900 hover:bg-primary-100"
              : "text-slate-700 hover:bg-slate-50",
          )}
          size="icon"
          variant="outline"
        >
          <ListFilterIcon className="h-4 w-4" />
          {hasActiveFilter ? (
            <Badge className="absolute -right-1 -top-1 h-4 min-w-4 justify-center rounded-full border-white bg-primary-900 px-1 text-[10px] font-semibold leading-none text-white">
              {activeCount}
            </Badge>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-max min-w-0 p-2">
        <DropdownMenuLabel className={dropdownSectionLabelClassName}>
          Notification category
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="-mx-1 mb-1 mt-0" />
        {NOTIFICATION_CATEGORY_IDS.map((categoryId) => {
          const { label, Icon } = notificationCategoryToMeta[categoryId];

          return (
            <FilterOptionItem
              key={categoryId}
              Icon={Icon}
              isSelected={selectedCategoryIds.has(categoryId)}
              label={label}
              onToggle={() => {
                toggleCategory(categoryId);
              }}
            />
          );
        })}
        <DropdownMenuLabel
          className={cn(dropdownSectionLabelClassName, "mt-1")}
        >
          Notification type
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="-mx-1 mb-1 mt-0" />
        {FILTERABLE_NOTIFICATION_TYPES.map((type) => {
          const meta = notificationTypeFilterToMeta[type];
          if (!meta) return null;

          return (
            <FilterOptionItem
              key={type}
              Icon={meta.Icon}
              isSelected={selectedTypes.has(type)}
              label={meta.label}
              onToggle={() => {
                toggleType(type);
              }}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FilterOptionItem = ({
  Icon,
  isSelected,
  label,
  onToggle,
}: {
  Icon: LucideIcon;
  isSelected: boolean;
  label: string;
  onToggle: () => void;
}) => {
  const render = () => (
    <DropdownMenuItem
      className={cn(
        "gap-2.5 rounded-md px-3 py-2 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        isSelected && "bg-slate-50 font-medium",
      )}
      onSelect={(e) => {
        e.preventDefault();
        onToggle();
      }}
    >
      <Icon className="h-4 w-4 shrink-0 text-slate-600" />
      <span className="min-w-0 flex-1 whitespace-nowrap">{label}</span>
      <span className="ml-3 flex h-4 w-4 shrink-0 items-center justify-center">
        <CheckIcon
          className={cn(
            "h-4 w-4 text-primary-700",
            isSelected ? "opacity-100" : "opacity-0",
          )}
        />
      </span>
    </DropdownMenuItem>
  );

  return render();
};
